import { useMutation } from '@tanstack/react-query';

import { apiService } from '../../services/api.service';
import { undoSpreadsheetChange } from '../../services/api/spreadsheets.api';

/**
 * A refused or failed undo. `message` is the backend's, written for the
 * researcher, so the card shows it as is; `code` picks what the card offers
 * next (`SPREADSHEET_EDITED_SINCE`, `SPREADSHEET_UNDO_UNCONFIRMED`, ...).
 */
export class SpreadsheetUndoError extends Error {
  constructor(
    message: string,
    public readonly code: string,
    /** HTTP status; absent when no response came back at all. */
    public readonly status?: number,
    /** False when the server sent no message and `message` is the fallback. */
    public readonly hasServerMessage = true,
  ) {
    super(message);
    this.name = 'SpreadsheetUndoError';
  }
}

/**
 * A refusal with no message of its own. Neutral: the card may be hiding Undo
 * for that refusal, so it must not invite a retry.
 */
export const UNDO_FALLBACK_MESSAGE =
  'Bower could not undo this change. Use version history to restore an earlier version.';

export interface UndoSpreadsheetChangeArgs {
  readonly artifactId: string;
  readonly token: string;
}

/**
 * Undo one agent batch (phase 3). One attempt only: `undoSpreadsheetChange`
 * sends it with no retry, and the mutation adds none (TanStack's mutation
 * default is 0; stated here so nobody raises it).
 */
export function useUndoSpreadsheetChange() {
  return useMutation({
    mutationFn: async ({ artifactId, token }: UndoSpreadsheetChangeArgs) => {
      const response = await undoSpreadsheetChange(apiService, artifactId, { token });
      if (!response.success || !response.data) {
        throw new SpreadsheetUndoError(
          response.error?.message || UNDO_FALLBACK_MESSAGE,
          response.error?.code ?? 'UNKNOWN_ERROR',
          response.error?.status,
          Boolean(response.error?.message),
        );
      }
      return response.data;
    },
    retry: 0,
  });
}
